<script setup>
  import { computed, ref, watch, onMounted, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const emit = defineEmits(['saved']);

  const model = defineModel();

  const newPageCarousel = {
    carousel_id: '',
    title: '',
    start_date: null,
    end_date: null
  };

  const props = defineProps({
    cohort_page_id: {
      type: Number,
      required: true
    }
  });

  const pageCarousel = ref(newPageCarousel);
  const carouselInput = ref(null);
  const errors = ref(null);
  const carousels = ref([]);

  const loadPageCarousel = async () => {
    try {
      const res = await fetch(`/admin/cohort_pages/${props.cohort_page_id}/page_carousels/${model.value}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      pageCarousel.value = (await res.json()).page_carousel
    } catch (error) {
      console.error(`Error fetching page carousel ${model.value}:`, error);
      pageCarousel.value = { ...newPageCarousel };
    }
  };

  const loadCarousels = async () => {
    try {
      const res = await fetch(`/admin/carousels.json?cohort_page_id=${props.cohort_page_id}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      carousels.value = (await res.json()).carousels
    } catch (error) {
      console.error(`Error fetching carousels:`, error);
      carousels.value = [];
    }
  };

  onMounted(async () => {
    await loadCarousels();
    if (model.value != 0) {
      await loadPageCarousel();
    }
    nextTick(() => {
      carouselInput.value.focus();
    });
  });

  const cancel = () => {
    model.value = null;
  };

  const save = async () => {
    const url = creationMode.value ? `/admin/cohort_pages/${props.cohort_page_id}/page_carousels.json` : `/admin/cohort_pages/${props.cohort_page_id}/page_carousels/${model.value}.json`;
    const method = creationMode.value ? 'POST' : 'PUT';
    try {
      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ page_carousel: pageCarousel.value, cohort_page_id: props.cohort_page_id })
      });
      if (res.ok) {
        emit('saved');
      } else {
        errors.value = (await res.json()).errors.join(', ');
      }
    } catch (error) {
      console.error(`Error ${creationMode.value ? 'creating' : 'updating'} page carousel:`, error);
    }
  };

  const creationMode = computed(() => model.value === 0);
</script>

<template>
  <div class="row p-3">
    <div class="col-6">
      <h3 class="mb-3">{{ creationMode ? t('cohort_page_carousels.new_carousel') : t('cohort_page_carousels.edit_carousel') }}</h3>
      <div class="alert alert-danger" role="alert" v-if="errors">
        {{ errors }}
      </div>
      <div class="mb-3 row">
        <label for="carousel_id" class="col-4 col-form-label">{{ t('cohort_page_carousels.carousel') }}</label>
        <div class="col-8">
          <select class="form-select" v-model="pageCarousel.carousel_id" id="carousel_id" ref="carouselInput">
            <option v-for="carousel in carousels" :key="carousel.id" :value="carousel.id">{{ carousel.title }}</option>
          </select>
        </div>
      </div>
      <div class="mb-3 row">
        <label for="start_date" class="col-4 col-form-label">{{ t('cohort_page_carousels.start_date') }}</label>
        <div class="col-4">
          <input type="date" class="form-control" id="start_date" v-model="pageCarousel.start_date">
        </div>
      </div>
      <div class="mb-3 row">
        <label for="end_date" class="col-4 col-form-label">{{ t('cohort_page_carousels.end_date') }}</label>
        <div class="col-4">
          <input type="date" class="form-control" id="end_date" v-model="pageCarousel.end_date">
        </div>
      </div>
      <div class="row">
        <div class="col d-flex justify-content-end">
          <button class="btn btn-primary me-2" @click="save">{{ creationMode ? t('cohort_page_carousels.create') : t('cohort_page_carousels.update') }}</button>
          <button class="btn btn-secondary" @click="cancel">{{ t('cohort_page_carousels.cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
</style>

<i18n src="../locales/en.json" global></i18n>
<i18n src="../locales/fr.json" global></i18n>
<i18n src="../locales/es.json" global></i18n>
<i18n src="../locales/it.json" global></i18n>
<i18n src="../locales/pt.json" global></i18n>
<i18n src="../locales/de.json" global></i18n>
